import { motion, useInView, useAnimate, stagger } from 'framer-motion';
import { useContext, useEffect } from 'react';
import { PageContext } from '../../contexts';
import { useInViewScroll, useSanitizedTranslation } from '../../hooks';
import { CTA, IImage } from '../../types';
import { IFormField, XumoForm } from 'ui';
import { useScrolledTheme } from 'ui';
import { handleSubmitProps } from 'ui';

export interface ModalFormProps {
  key?: string;
  heading: string;
  body: string;
  formFields: IFormField[];
  formAction: handleSubmitProps;
  isOpen: boolean;
  isDark: boolean;
  onClose: () => void;
  CTA: CTA;
  backgroundColor?: string;
  bottomBackgroundColor?: string;

  styleOverrides?: {
    form?: string;
    formField?: string;
    CTA?: {
      general?: string;
      disabledColor?: string;
      enabledColor?: string;
      alignment?: string;
    };
    hideLabel?: boolean;
  };
}

export function ModalForm({ isDark, isOpen, onClose, CTA, heading, body, formFields, formAction, backgroundColor, styleOverrides }: ModalFormProps) {
  const { ref, progress: y } = useInViewScroll([200, 0], ['start 100vh', 'end 0vh']);
  const { pageCtx } = useContext(PageContext);
  const { t } = useSanitizedTranslation();

  const { host } = pageCtx;

  const { componentInView } = useScrolledTheme({
    ref,
    background: backgroundColor,
    // TODO: add accent to directus
    accent: '#000000',
    text: '#000000',
  });

  const [viewref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        '.formwrapper',
        {
          y: [50, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 0.9,
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      if (isOpen) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [isOpen]);

  if (isOpen) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 top-0 z-50 flex h-screen w-screen items-center justify-center overflow-hidden bg-black bg-opacity-70 p-4"
        data-testid={'lead-form-section'}
      >
        <div
          className={`hide-scroll relative h-fit max-h-[90vh] w-full max-w-4xl overflow-auto rounded-lg ${isDark ? 'bg-xumoDarkPlum' : 'bg-white shadow-xl'}`}
          style={{ boxShadow: '0 0 80px 0 #5B2E4F' }}
        >
          <div className="absolute right-4 top-4 z-50 flex items-center">
            <button onClick={onClose} className={`flex h-8 w-8 items-center justify-center rounded-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div ref={ref} className="relative z-40 grid w-full grid-cols-1 grid-rows-1 items-center justify-center">
            {/* Form wrapper */}
            <div className="relative flex w-full flex-col items-center" ref={viewref}>
              <div
                className={`form.modal content-wrapper grow-1 formwrapper z-20 flex w-full flex-col items-center justify-center rounded-lg ${
                  isDark ? 'bg-xumoDarkPlum text-white' : 'bg-white text-black'
                }`}
              >
                <div
                  className="type-subheading-light mb-8 px-12 text-center md:mb-0 md:px-0"
                  data-i18n={heading}
                  dangerouslySetInnerHTML={{ __html: t(heading) }}
                />

                {body && <div className="type-body allow-html p-6 text-center lg:w-1/2" data-i18n={body} dangerouslySetInnerHTML={{ __html: t(body) }} />}

                <div className="w-full">
                  <XumoForm
                    formFields={formFields}
                    CTA={CTA}
                    formAction={{
                      submissionEndpoint: formAction?.submissionEndpoint || '/api/contact',
                      successMessage: formAction?.successMessage || '',
                      onSuccess: formAction?.onSuccess,
                      formType: formAction?.formType,
                    }}
                    styleOverrides={{
                      CTA: {
                        general: `text-black bg-white border-transparent py-2 px-12`,
                        disabledColor: 'bg-xumoAltBerryDark',
                        enabledColor: 'bg-xumoAltBerryLight',
                      },
                      formField: `w-full bg-transparent`,
                      form: 'w-full justify-center items-center px-8 sm:px-16 sm:pt-8 gap-4',
                      ...styleOverrides,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
}
